// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import generalStyles from "@keller/App/SharedStyle/GeneralStyles";
import { xPartNumberStyle } from "@keller/App/SharedStyle/ProductPartNumberStyle";
import { ProductPriceStyles } from "@keller/Overrides/Components/ProductPrice";
import { constants } from "@keller/XDL/constants";
import List from "@keller/XDL/Icons/List";
import { fontConfig } from "@keller/XDL/PreStyle/typography";
import {
    avoidBreakInsidePrintElement,
    color,
    hideElementOnPrint,
    iconLinkCommonStyles,
    mergeWithCss,
    xScreen,
} from "@keller/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    header?: {
        wrapper?: InjectableCss;
        title?: TypographyPresentationProps;
    };
    productImage?: ProductImageStyles;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    removeCartLineLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeText?: TypographyPresentationProps;
    readOnlyNotes?: SmallHeadingAndTextStyles;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    header: {
        wrapper: {
            css: css`
                margin-bottom: 12px;
                display: flex;
                align-items: flex-end;
                ${avoidBreakInsidePrintElement}
            `,
        },
        title: {
            variant: "h4",
            css: css`
                line-height: 20px;
                margin-bottom: 0;
            `,
        },
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 88px;
                height: 88px;
                background-color: ${color("common.white")};
                justify-content: center;

                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    width: 64px;
                    height: 64px;
                }
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
                height: auto;
            `,
        },
    },
    productBrand: {
        nameLink: {
            color: "primary",
            typographyProps: {
                size: 12,
                weight: "normal",
                css: css`
                    line-height: 15px;
                    letter-spacing: 0.5px;
                `,
            },
            css: css`
                margin-bottom: 4px;
                display: inline-block;

                ${hideElementOnPrint}
            `,
        },
        nameText: {
            size: 12,
            weight: "normal",
            css: css`
                line-height: 15px;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
                display: inline-block;
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                size: 12,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 15px;
                `,
            },
            css: css`
                display: inline-block;
                margin-bottom: 24px;
                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    margin-bottom: 8px;
                }

                @media print {
                    width: 100%
                }
            `,
        },
        productDetailDescription: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 15px;
                margin-bottom: 24px;
                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    margin-bottom: 8px;
                }
            `,
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                margin-bottom: 16px;
                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    margin-bottom: 0;
                }

                ${GridItemStyle} {
                    @media print {
                        width: unset;
                        -webkit-flex-basis: unset;
                        flex-basis: unset;
                        max-width: unset;
                    }
                }
                ${GridOffset} {
                    @media print {
                        display: flex;
                        flex-direction: row;
                        flex-wrap: wrap;
                        gap: 0 16px;
                        margin-top: 2px;
                    }
                }

                @media print {
                    width: 100%;
                }
            `,
        },
    },
    addToListLink: {
        ...mergeWithCss(iconLinkCommonStyles(List), {
            css: css`
                white-space: nowrap;
            `,
        }),
    },
    removeCartLineLink: {
        ...iconLinkCommonStyles(X),
    },
    productPrice: {
        wrapper: {
            css: css`
                @media print {
                    width: 120px;
                }
            `,
        },
        priceLabelText: {
            ...mergeWithCss(generalStyles.xSmallHeadingAndTextStyles?.heading, {
                css: css`
                    margin-bottom: 8px;
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: none;
                    }

                    @media print {
                        font-size: 12px;
                        line-height: 18px;
                        font-weight: ${fontConfig.weight.bold};
                        margin-bottom: 0;
                    }
                `,
            }),
        },
        priceWrapper: {
            css: css`
                text-align: center;
                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    text-align: left;
                    margin-bottom: 8px;
                }
                @media print {
                    line-height: 18px;
                    font-size: 12px;
                }
            `,
        },
        price: {
            priceText: {
                size: 14,
                weight: "bold",
                css: css`
                    line-height: 19px;
                    margin-right: 0;
                    @media print {
                        font-weight: ${fontConfig.weight.regular};
                    }
                `,
            },
            errorText: {
                size: 12,
                lineHeight: "19px",
                weight: "bold",
                css: css`
                    line-height: 18px;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 12,
                    weight: "normal",
                    css: css`
                        text-transform: uppercase;
                        line-height: 18px;
                    `,
                },
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    justify-content: flex-end;
                    align-items: flex-end;
                    flex-direction: column;
                `,
            },
            heading: {
                ...mergeWithCss(generalStyles.xSmallHeadingAndTextStyles?.heading, {
                    css: css`
                        margin-bottom: 10px;
                        ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                            display: none;
                        }
                        @media print {
                            font-size: 12px;
                            line-height: 18px;
                            font-weight: ${fontConfig.weight.bold};
                            text-align: left;
                            margin-bottom: 0;
                        }
                    `,
                }),
            },
            text: {
                size: 14,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 19px;
                    text-align: right;
                    display: flex;
                    @media print {
                        font-weight: ${fontConfig.weight.regular};
                        margin-left: 0;
                    }
                `,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
            &.readonly {
                flex-direction: row;
            }
        `,
    },
    costCodeLabelText: {
        ...mergeWithCss(xPartNumberStyle.label, {
            css: css`
                margin-bottom: 10px;
            `,
        }),
    },
    costCodeText: {
        ...mergeWithCss(xPartNumberStyle.value, {
            css: css`
                margin-bottom: 10px;
            `,
        }),
    },
    readOnlyNotes: {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    margin-bottom: 16px;
                `,
            },
            heading: {
                ...generalStyles.xSmallHeadingAndTextStyles?.heading,
            },
            text: {
                ...xPartNumberStyle.value,
            },
        },
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 16px;
            & > div:not(:last-child) {
                margin-bottom: 5px;
            }
        `,
    },
    promotionNameLabel: {
        ...xPartNumberStyle.label,
    },
    promotionNameText: {
        ...xPartNumberStyle.value,
    },
    quantityTextField: {
        cssOverrides: {
            formField: css`
                & input:not(.react-datetime-picker__inputGroup__input):disabled {
                    color: ${color("text.main", 0.9)};
                    @media print {
                        color: ${color("text.main")};
                        font-size: 14px;
                    }
                }
            `,
            formInputWrapper: css`
                &&&& {
                    margin: 0;
                    input {
                        padding: 0;
                        @media print {
                            border: none;
                            background-color: ${color("common.white")};
                            text-align: left;
                            height: auto;
                        }
                    }
                }
            `,
            inputSelect: css`
                height: 27px;
                width: 55px;
                text-align: center;
            `,
        },
        labelProps: {
            variant: "legend",
            size: 12,
            weight: "normal",
            css: css`
                line-height: 14px;
                text-transform: uppercase;
                margin-bottom: 8px;
                min-height: auto;
                ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                    display: none;
                }
                @media print {
                    font-size: 12px;
                    line-height: 18px;
                    font-weight: ${fontConfig.weight.bold};
                    text-transform: capitalize;
                    margin-bottom: 0;
                }
            `,
        },
    },
};

cartLineSharedStyles.priceHeadingAndText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText, {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    flex-direction: column;
                    ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                        align-items: flex-start;
                    }
                    @media print {
                        width: 120px;
                    }
                `,
            },
            heading: {
                css: css`
                    @media print {
                        font-size: 12px;
                        line-height: 18px;
                        font-weight: ${fontConfig.weight.bold};
                        margin-bottom: 0;
                        width: 120px;
                    }
                `
            },
            text: {
                css: css`
                    @media print {
                        margin-left: 0;
                        font-weight: ${fontConfig.weight.regular};
                    }
                    `
            }
        },
    }),
};

cartLineSharedStyles.quantityHeadingAndText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText, {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    justify-content: flex-start;
                    align-items: flex-start;
                    flex-direction: column;
                `,
            },
        },
    } as SmallHeadingAndTextStyles),
};

export default cartLineSharedStyles;
